'use client';

import useUserProfile from '@/hooks/useUserProfile';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';
import { cloneDeep, first, isEmpty, last, size } from 'lodash-es';
import { fetchViaProxy } from './proxy-fetch';
import { API_BASE_URL } from '@/utils/constants';
import { useUpdatePostInCache } from './feed.actions';

/**************** PRIVATE FUNCTIONS AND FETECHERS ******************** */

const createPlaceholderComment = (
  postId,
  parentCommentId,
  message,
  attachment,
  userProfile,
  selectedProfile,
) => {
  return {
    id: Date.now().toString(),
    postId: postId,
    message: message,
    postAs: selectedProfile,
    parentCommentId: parentCommentId,
    attachmentUrl: attachment?.content,
    createdOn: new Date().toISOString(),
    replies: [],
    totalReplies: 0,
    reactions: {},
    totalReactions: 0,
    userReaction: {
      hasReaction: false,
      reactionType: null,
      as: null,
    },
    author: {
      id: userProfile.id,
      first_name: userProfile.firstName,
      last_name: userProfile.lastName,
      avatar: userProfile.avatar,
      orgDetails: userProfile.activeCommunity,
    },
  };
};

const createPostAttachment = async (postId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(
    `/proxy/v1/posts/${postId}/comments/attachment`,
    {
      method: 'POST',
      body: formData,
    },
    true,
  );

  if (res.ok) {
    const data = await res.json();
    return data ? data.url : null;
  }
  return null;
};

const updateCommentCache = (comment, queryClient, isNew) => {
  const isReply = !!comment.parentCommentId;
  const queryKey = getQueryKey(comment.postId, comment.parentCommentId);
  const { postId, id: commentId, parentCommentId } = comment;
  queryClient.setQueryData(queryKey, (oldData) => {
    if (oldData) {
      return produce(oldData, (draft) => {
        if (isNew) {
          const firstPage = first(draft.pages);
          if (isReply) {
            firstPage.comments = [...firstPage.comments, comment];
          } else {
            firstPage.comments = [comment, ...firstPage.comments];
          }
        } else {
          const { pageIndex, commentIndex, comment } = getCommentFromCache(
            postId,
            commentId,
            parentCommentId,
            queryClient,
          );

          if (pageIndex >= 0 && commentIndex >= 0) {
            draft.pages[pageIndex].comments[commentIndex] = comment;
          } else {
            // this should never happen
            console.warn('Failed to update comment cache');
          }
        }
      });
    } else {
      return {
        pages: [
          {
            comments: [comment],
            hasMore: false,
          },
        ],
      };
    }
  });
};

const getQueryKey = (postId, parentCommentId) => {
  const isReply = !!parentCommentId;
  return isReply
    ? ['post-comment-replies', parentCommentId]
    : ['post-comments', postId];
};

const getCommentFromCache = (
  postId,
  commentId,
  parentCommentId,
  queryClient,
) => {
  const queryKey = getQueryKey(postId, parentCommentId);
  const cache = queryClient.getQueryData(queryKey);
  if (cache) {
    const pageIndex = cache.pages.findIndex((page) => {
      return page.comments.some((c) => c.id === commentId);
    });
    if (pageIndex >= 0) {
      const commentIndex = cache.pages[pageIndex].comments.findIndex(
        (c) => c.id === commentId,
      );
      return {
        comment: cache.pages[pageIndex].comments[commentIndex],
        commentIndex,
        pageIndex,
      };
    }
  }
  return {
    comment: null,
    pageIndex: -1,
    commentIndex: -1,
  };
};

const removeCommentFromCache = (comment, queryClient) => {
  const { pageIndex } = getCommentFromCache(
    comment.postId,
    comment.id,
    comment.parentCommentId,
    queryClient,
  );

  if (pageIndex >= 0) {
    const queryKey = getQueryKey(comment.postId, comment.parentCommentId);

    queryClient.setQueryData(queryKey, (oldData) => {
      return produce(oldData, (draft) => {
        draft.pages[pageIndex].comments = draft.pages[
          pageIndex
        ].comments.filter((c) => c.id !== comment.id);
      });
    });
  } else {
    // this should never happen
    console.warn('Failed to remove comment reply from cache');
  }
};

const fetchPostComments = async (postId, { page, before }) => {
  const params = new URLSearchParams({
    limit: page === 0 ? 2 : 10,
  });
  if (before) {
    params.append('before', before);
  }

  const { ok, data } = await fetchViaProxy(
    `/proxy/v1/posts/${postId}/comments?${params.toString()}`,
  );

  if (!ok) {
    throw new Error(`Error fetching comments for post ${postId}`);
  }

  return data;
};

const fetchPostReplies = async (postId, parentCommentId, before) => {
  const params = new URLSearchParams({
    limit: 5,
  });
  if (before) {
    params.append('before', before);
  }

  const { ok, data } = await fetchViaProxy(
    `/proxy/v1/posts/${postId}/comments/${parentCommentId}?${params.toString()}`,
  );

  if (!ok) {
    throw new Error(
      `Error fetching comment replies for comment ${parentCommentId} on post ${postId}`,
    );
  }

  return data;
};

/*************** HOOKS AND PUBLIC FUNCTIONS ********************** */

export const useFetchPostComments = (post) => {
  return useInfiniteQuery({
    queryKey: ['post-comments', post.id],
    queryFn: ({ pageParam = { page: 0 } }) =>
      fetchPostComments(post.id, pageParam),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage && !lastPage.hasMore) {
        return undefined;
      }

      const lastCommentDate = last(lastPage.comments)?.createdOn;
      return {
        page: pages.length,
        before: lastCommentDate,
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};

export const useFetchPostCommentReplies = (post, parentComment) => {
  return useInfiniteQuery({
    queryKey: ['post-comment-replies', parentComment.id],
    queryFn: async ({ pageParam }) => {
      return fetchPostReplies(post.id, parentComment.id, pageParam);
    },
    enabled: !!parentComment && size(parentComment.replies) > 0,
    initialData: () => {
      const numReplies = size(parentComment.replies);
      if (numReplies === 0) {
        return undefined;
      }
      return {
        pages: [
          {
            comments: parentComment.replies,
            hasMore: numReplies < parentComment.totalReplies,
          },
        ],
      };
    },
    staleTime: Infinity,
    cacheTime: Infinity,
    getNextPageParam: (lastPage) => {
      if (!lastPage || !lastPage.hasMore) {
        return undefined;
      }
      return first(lastPage.comments)?.createdOn;
    },
  });
};

export const useAddOrUpdatePostComment = () => {
  const userProfile = useUserProfile();
  const selectedProfile = userProfile?.selectedProfile;
  const queryClient = useQueryClient();
  const { mutate: updatePost } = useUpdatePostInCache();

  return useMutation({
    mutationKey: ['add-post-comment'],
    mutationFn: async ({
      postId,
      parentCommentId,
      message,
      attachment,
      commentId,
    }) => {
      const isUpdate = !!commentId;
      const isReply = !!parentCommentId;
      const commentData = {
        postId: postId,
        message: message,
        postAs: selectedProfile,
      };

      // follow up with the actual update
      if (attachment?.file) {
        const fileUrl = await createPostAttachment(postId, attachment.file);
        if (fileUrl) {
          commentData.attachmentUrl = fileUrl;
        } else {
          throw new Error('Error uploading attachment');
        }
      } else if (
        attachment?.content &&
        attachment?.content?.startsWith(API_BASE_URL)
      ) {
        commentData.attachmentUrl = attachment.content.replace(
          `${API_BASE_URL}/v1`,
          '',
        );
      } else {
        commentData.attachmentUrl = null;
      }

      let path = `/proxy/v1/posts/${postId}/comments`;
      if (isUpdate) {
        path = `${path}/${commentId}`;
      } else if (isReply) {
        path = `${path}/${parentCommentId}`;
      }

      const res = await fetchViaProxy(path, {
        method: isUpdate ? 'PUT' : 'POST',
        body: JSON.stringify(commentData),
      });

      if (!res.ok) {
        throw new Error(`Error adding comment to post ${postId}`);
      }

      return res.data;
    },
    onMutate: async ({
      postId,
      parentCommentId,
      message,
      attachment,
      commentId,
    }) => {
      const isUpdate = !!commentId;

      const fromCache = getCommentFromCache(
        postId,
        commentId,
        parentCommentId,
        queryClient,
      );

      let comment = createPlaceholderComment(
        postId,
        parentCommentId,
        message,
        attachment,
        userProfile,
        selectedProfile,
      );
      if (isUpdate) {
        comment = {
          ...fromCache.comment,
          message,
          attachmentUrl: attachment?.content,
        };
      }

      // do an optimistic update
      updateCommentCache(comment, queryClient, !isUpdate);

      return {
        optimisticData: comment,
        cached: fromCache.comment,
      };
    },
    onError: (_err, { commentId }, context) => {
      const { optimisticData, cached } = context;
      const isUpdate = !!commentId;
      if (isUpdate) {
        updateCommentCache(cached, queryClient, false);
      } else {
        removeCommentFromCache(optimisticData, queryClient);
      }
    },
    onSuccess: (serverData, _var, context) => {
      // apply the server response to the cache
      const { optimisticData } = context;

      // TODO - Shyam - handle reward
      const { comment, reward, totalComments } = serverData;

      // replace the optimistic data with the server data
      const { pageIndex, commentIndex } = getCommentFromCache(
        optimisticData.postId,
        optimisticData.id,
        optimisticData.parentCommentId,
        queryClient,
      );

      if (pageIndex >= 0 && commentIndex >= 0) {
        const queryKey = getQueryKey(comment.postId, comment.parentCommentId);
        queryClient.setQueryData(queryKey, (oldData) => {
          return produce(oldData, (draft) => {
            draft.pages[pageIndex].comments[commentIndex] = comment;
          });
        });

        // update the post cache
        updatePost({
          postId: comment.postId,
          props: [{ key: 'totalComments', value: totalComments }],
        });
      } else {
        // this should never happen
        console.warn('Failed to update comment cache');
      }
    },
  });
};

export const useDeletePostComment = () => {
  const queryClient = useQueryClient();
  const { mutate: updatePost } = useUpdatePostInCache();

  return useMutation({
    mutationKey: ['delete-post-comment'],
    mutationFn: async (comment) => {
      const { postId, id: commentId } = comment;
      const { ok, data } = await fetchViaProxy(
        `/proxy/v1/posts/${postId}/comments/${commentId}`,
        {
          method: 'DELETE',
        },
      );

      if (!ok) {
        throw new Error(`Error deleting comment ${commentId}`);
      }
      return data.totalComments;
    },
    onMutate: async (comment) => {
      // update the state
      const { pageIndex, commentIndex } = getCommentFromCache(
        comment.postId,
        comment.id,
        comment.parentCommentId,
        queryClient,
      );
      removeCommentFromCache(comment, queryClient);
      return { comment, pageIndex, commentIndex };
    },
    onError: (_err, _vars, context) => {
      const { comment, pageIndex, commentIndex } = context;
      const queryKey = getQueryKey(comment.postId, comment.parentCommentId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          // insert the comment back into the cache immutably
          draft.pages[pageIndex].comments = [
            ...draft.pages[pageIndex].comments.slice(0, commentIndex),
            comment,
            ...draft.pages[pageIndex].comments.slice(commentIndex),
          ];
        });
      });
    },
    onSuccess: (totalComments, { postId }) => {
      // update the post cache
      updatePost({
        postId: postId,
        props: [{ key: 'totalComments', value: totalComments }],
      });
    },
  });
};

export const useReportPostComment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['report-post-comment'],
    mutationFn: async (comment) => {
      const { postId, id: commentId } = comment;
      const res = await fetchViaProxy(
        `/proxy/v1/posts/${postId}/comments/${commentId}/report`,
        {
          method: 'POST',
        },
      );

      if (!res.ok) {
        throw new Error(`Error reporting comment ${commentId}`);
      }
    },
    onMutate: async (comment) => {
      // update the state
      const { pageIndex, commentIndex } = getCommentFromCache(
        comment.postId,
        comment.id,
        comment.parentCommentId,
        queryClient,
      );
      removeCommentFromCache(comment, queryClient);
      return { comment, pageIndex, commentIndex };
    },
    onError: (_err, _vars, context) => {
      const { comment, pageIndex, commentIndex } = context;
      const queryKey = getQueryKey(comment.postId, comment.parentCommentId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          // insert the comment back into the cache immutably
          draft.pages[pageIndex].comments = [
            ...draft.pages[pageIndex].comments.slice(0, commentIndex),
            comment,
            ...draft.pages[pageIndex].comments.slice(commentIndex),
          ];
        });
      });
    },
  });
};

export const useUpdateCommentReaction = () => {
  const userProfile = useUserProfile();
  const selectedProfile = userProfile?.selectedProfile;
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: 'update-comment-reaction',
    mutationFn: async ({ comment, reaction }) => {
      const pathPart = reaction ? 'like' : 'unlike';
      const { postId, id: commentId } = comment;
      const path = `/proxy/v1/posts/${postId}/comments/${commentId}/${pathPart}`;

      const { data, ok } = await fetchViaProxy(path, {
        method: 'POST',
        body: JSON.stringify({
          as: selectedProfile,
          likeType: reaction,
        }),
      });

      if (!ok) {
        throw new Error(`Error reacting to post ${postId}`);
      }
      return data;
    },
    onMutate: ({ comment, reaction }) => {
      const hasReaction = !isEmpty(reaction);
      const { postId, id: commentId, parentCommentId } = comment;
      const fromCache = getCommentFromCache(
        postId,
        commentId,
        parentCommentId,
        queryClient,
      );
      const hadReaction = fromCache.comment.userReaction.hasReaction;
      const cloned = cloneDeep(fromCache.comment);
      cloned.userReaction = {
        hasReaction: hasReaction,
        reactionType: reaction,
        as: hasReaction ? selectedProfile : null,
      };

      if (!hadReaction && hasReaction) {
        cloned.totalReactions += 1;
      } else if (hadReaction && !hasReaction) {
        cloned.totalReactions -= 1;
      }

      // do an optimistic update
      updateCommentCache(cloned, queryClient, false);

      return {
        cached: fromCache.comment,
      };
    },
    onSuccess: (serverData, { comment }) => {
      const { postId, id: commentId, parentCommentId } = comment;
      // TODO: Shyam - handle reward
      const { userReaction, totalReactions, reactions, reward } = serverData;

      // apply the server response to the cache
      const queryKey = getQueryKey(postId, parentCommentId);
      queryClient.setQueryData(queryKey, (oldData) => {
        return produce(oldData, (draft) => {
          const { pageIndex, commentIndex } = getCommentFromCache(
            postId,
            commentId,
            parentCommentId,
            queryClient,
          );

          if (pageIndex >= 0 && commentIndex >= 0) {
            const inCache = draft.pages[pageIndex].comments[commentIndex];
            inCache.userReaction = userReaction;
            inCache.totalReactions = totalReactions;
            inCache.reactions = reactions;
          } else {
            // this should never happen
            console.warn('Failed to update comment cache');
          }
        });
      });
    },
    onError: (err, args, context) => {
      const { cached } = context;
      updateCommentCache(cached, queryClient, false);
    },
  });
};
